import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { onAnimationEnd, requestIdleCallback, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {Element | null} */
  #buyButtonsBlock = null;

  /** @type {Element | null} */
  #footer = null;

  /** @type {MutationObserver | null} */
  #targetsObserver = null;

  /** Hide the sticky bar this many px before it reaches the footer, avoiding overlap there. */
  #footerOffsetPx = 200;

  connectedCallback() {
    super.connectedCallback();

    this.#ensureTargetsReady();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetsObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Resolves buy-buttons + footer once they exist, then wires IntersectionObservers.
   * Retries on DOM mutations — blocks inside product-details can mount after the sticky bar.
   */
  #ensureTargetsReady() {
    if (this.#tryResolveTargets()) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#targetsObserver = new MutationObserver(() => {
      if (this.#tryResolveTargets()) this.#targetsObserver?.disconnect();
    });
    this.#targetsObserver.observe(section, { childList: true, subtree: true });

    requestAnimationFrame(() => this.#tryResolveTargets());
    requestIdleCallback(() => this.#tryResolveTargets());
  }

  /** @returns {boolean} Whether buy-buttons were found and observers are wired. */
  #tryResolveTargets() {
    this.#resolveTargets();
    if (!this.#buyButtonsBlock) return false;

    if (this.#footer && !this.#buyButtonsIntersectionObserver) {
      this.#setupIntersectionObserver();
    }

    return true;
  }

  /** Caches buy-buttons + footer targets used by visibility tracking. */
  #resolveTargets() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;

    if (section && !this.#buyButtonsBlock) {
      this.#buyButtonsBlock =
        section.querySelector('.buy-buttons-block') ??
        section.querySelector('[ref="addToCartButton"]')?.closest('.buy-buttons-block') ??
        null;
    }

    if (!this.#footer) {
      this.#footer = this.#resolvePageFooter();
    }

    if (!this.#targetAddToCartButton) {
      const productForm =
        section && productId
          ? section.querySelector(`product-form-component[data-product-id="${productId}"]`)
          : this.#getProductForm();

      if (productForm) {
        this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      }
    }
  }

  /**
   * Page footer only — `document.querySelector('footer')` matches the cart drawer
   * footer first, which sits at the top of the viewport and breaks visibility logic.
   * @returns {Element | null}
   */
  #resolvePageFooter() {
    return (
      document.querySelector('footer.shopify-section-group-footer-group') ??
      document.querySelector('.shopify-section-group-footer-group footer') ??
      [...document.querySelectorAll('footer')].find(
        (el) => !el.closest('cart-drawer-component, cart-drawer, dialog-component, quick-add-dialog'),
      ) ??
      null
    );
  }

  /**
   * Sets up IntersectionObservers for buy-buttons reveal and footer hide.
   * The buy-buttons observer only reveals the bar once the ATC has fully left the viewport,
   * so the two CTAs are never visible at the same time.
   */
  #setupIntersectionObserver() {
    const buyButtonsBlock = this.#buyButtonsBlock;
    const footer = this.#footer;
    if (!buyButtonsBlock || !footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.top < window.innerHeight) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: `${this.#footerOffsetPx}px 0px 0px 0px`,
      },
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const mode = this.refs.addToCartButton.dataset.ctaMode || 'add';

    // Follows the PDP buy-buttons state machine: notify opens the back-in-stock
    // dialog, sold-out/unavailable are inert, only "add" puppets the PDP ATC.
    if (mode === 'notify') {
      this.#openNotifyDialog();
      return;
    }

    if (mode !== 'add') return;

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const { addToCartButton } = this.refs;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Opens the global Klaviyo back-in-stock dialog for the current variant,
   * mirroring `notify-me-component`. No-op when BIS is disabled (no dialog).
   */
  #openNotifyDialog() {
    const button = this.refs.addToCartButton;
    const dialog = /** @type {any} */ (document.getElementById('klaviyo-bis-dialog'));
    if (!dialog?.openForVariant || !button.dataset.notifyVariantId) return;

    dialog.openForVariant({
      variantId: button.dataset.notifyVariantId,
      productTitle: button.dataset.productTitle,
      variantTitle: button.dataset.notifyVariantTitle || '',
    });
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';

    this.#patchStickyBarFromServer(newStickyBar);

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    if (newStickyAddToCart.dataset.stickyBarVisible != null) {
      this.dataset.stickyBarVisible = newStickyAddToCart.dataset.stickyBarVisible;
    }

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncPickerWithPdp());
    });

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  /**
   * Updates price, variant title, image, desktop ATC, and the mobile size picker.
   * @param {Element} newStickyBar
   */
  #patchStickyBarFromServer(newStickyBar) {
    const bar = this.refs.stickyBar;
    const hasSizePicker = bar.querySelector('.sticky-add-to-cart__picker') != null;

    this.#morphChild(bar, newStickyBar, '.sticky-add-to-cart__price');
    this.#morphChild(bar, newStickyBar, '.sticky-add-to-cart__variant');
    this.#morphChild(bar, newStickyBar, '.sticky-add-to-cart__image');

    // Full-element morph so the CTA's own attributes (data-cta-mode, disabled,
    // notify data) update with the variant, not just its children.
    const currentButton = bar.querySelector('.sticky-add-to-cart__button');
    const newButton = newStickyBar.querySelector('.sticky-add-to-cart__button');
    if (currentButton && newButton) {
      morph(currentButton, newButton, { childrenOnly: false });
    }

    if (hasSizePicker) {
      const currentPicker = bar.querySelector('quick-add-component.sticky-add-to-cart__picker');
      const newPicker = newStickyBar.querySelector('quick-add-component.sticky-add-to-cart__picker');

      if (currentPicker && newPicker) {
        const panel = currentPicker.querySelector('[data-sizes-panel]');
        const panelWasOpen = panel instanceof HTMLElement && panel.dataset.open === 'true' && !panel.hidden;

        morph(currentPicker, newPicker, { ...MORPH_OPTIONS, childrenOnly: false });

        if (panelWasOpen) {
          const morphedPanel = currentPicker.querySelector('[data-sizes-panel]');
          const trigger = currentPicker.querySelector('.sticky-add-to-cart__picker-trigger');
          if (morphedPanel instanceof HTMLElement) {
            morphedPanel.hidden = false;
            morphedPanel.dataset.open = 'true';
          }
          trigger?.setAttribute('aria-expanded', 'true');
        }
      }
    }
  }

  /**
   * @param {Element} currentBar
   * @param {Element} newBar
   * @param {string} selector
   */
  #morphChild(currentBar, newBar, selector) {
    const current = currentBar.querySelector(selector);
    const next = newBar.querySelector(selector);
    if (!current || !next) return;

    morph(current, next, { childrenOnly: true });
  }

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    const picker = this.querySelector('.sticky-add-to-cart__picker');
    if (picker && 'closeSizesPanel' in picker) {
      /** @type {{ closeSizesPanel: () => void }} */ (picker).closeSizesPanel();
    }
  }

  /** Re-syncs sticky size pills and region tabs with the PDP after bar morph. */
  #syncPickerWithPdp() {
    const picker = this.refs.stickyBar.querySelector('quick-add-component.sticky-add-to-cart__picker');
    if (!picker || !('syncSizeSelection' in picker)) return;

    const quickAdd = /** @type {{ syncSizeSelection: () => void; syncSizeRegionFromPdp?: () => void }} */ (picker);
    quickAdd.syncSizeSelection();
    quickAdd.syncSizeRegionFromPdp?.();
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const mode = addToCartButton.dataset.ctaMode || 'add';
    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Quantity is only meaningful for the add-to-cart state.
    if (mode === 'add' && available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
